import { useLayoutEffect, useState, type RefObject } from 'react';

/** ProductImage and "+N" badge are 50px wide; KIB rem baseline makes that 5rem. */
const THUMBNAIL_SIZE_REM = 5;

/** Default collapsed thumbnail gap: unit.rem(16px) / spacing.s4. */
const THUMBNAIL_GAP_REM = 1.6;

export interface UseMaxVisibleItemsOptions {
  /** Initial value before the first measurement. */
  initialMax?: number;
  /** Upper bound on visible thumbnails. */
  maxItems?: number;
  /** Thumbnail / badge size in rem. */
  itemSizeRem?: number;
  /** Gap between items in rem. */
  gapRem?: number;
}

/** Measures how many product thumbnails fit in the card. */
export function useMaxVisibleItems(
  containerRef: RefObject<HTMLElement | null>,
  {
    initialMax = 2,
    maxItems = 9,
    itemSizeRem = THUMBNAIL_SIZE_REM,
    gapRem = THUMBNAIL_GAP_REM,
  }: UseMaxVisibleItemsOptions = {},
): number {
  const [maxVisibleItems, setMaxVisibleItems] = useState(initialMax);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const calculate = () => {
      // Thumbnail sizes are rem-based, so use root font size for accessibility scaling.
      const rootFontSize =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const itemWidth = itemSizeRem * rootFontSize;
      const gapWidth = gapRem * rootFontSize;
      // Subtract 1px for sub-pixel / rounding issues.
      const cardWidth = container.offsetWidth - 1;
      const slotWidth = itemWidth + gapWidth;
      // Reserve one slot for the "+N" badge so it cannot wrap to a second row.
      const slotsIncludingBadge = Math.floor((cardWidth + gapWidth) / slotWidth);
      const thumbnails = slotsIncludingBadge - 1;

      setMaxVisibleItems(Math.min(maxItems, Math.max(1, thumbnails)));
    };

    calculate();

    const resizeObserver = new ResizeObserver(calculate);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, gapRem, itemSizeRem, maxItems]);

  return maxVisibleItems;
}
